/**
 * The small pieces every tool panel is built from.
 *
 * Written once so twenty panels share one field label, one empty state and one
 * result row. The eight panels that predate this file each invented their own,
 * which is how a 24px blue heading and a 16px corner radius ended up in a
 * 300px rail — a look nobody chose, that simply accumulated.
 */

import React from 'react';
import type { Layer } from '../../../types';
import { getLayerDisplayName } from '../../../layers/capabilities';

/** A labelled control. The label is the quiet half; the control is the point. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-slate-600">{label}</span>
      {hint && <span className="mt-0.5 block text-[10.5px] leading-snug text-slate-400">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

/** Two or three controls that belong on one line, in a 300px rail. */
export function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-end gap-2 [&>*]:min-w-[86px] [&>*]:flex-1">{children}</div>;
}

export const inputClass = 'gl-input w-full px-2.5 py-1.5 text-[12.5px]';
export const btnClass = 'gl-btn whitespace-nowrap px-3 py-1.5 text-[12.5px]';

/**
 * What a panel says when it has nothing to show yet.
 *
 * Always states the next action, never just "no data" — an empty state that
 * does not say what to do is a dead end with punctuation.
 */
export function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg bg-slate-50 px-2.5 py-3 text-[11.5px] leading-snug text-slate-500">
      {children}
    </p>
  );
}

/** A panel-level problem: the service cannot do this, the config is missing. */
export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn';
  children: React.ReactNode;
}) {
  const skin =
    tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-blue-100 bg-blue-50 text-blue-700';
  return (
    <p className={`rounded-lg border px-2.5 py-2 text-[11.5px] leading-snug ${skin}`}>{children}</p>
  );
}

/** A layer dropdown, scoped to whatever the tool's config allows. */
export function LayerSelect({
  layers,
  value,
  onChange,
  placeholder = 'Choose a layer…',
  id,
}: {
  layers: Layer[];
  value: string;
  onChange: (layerId: string) => void;
  placeholder?: string;
  id?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={inputClass}
    >
      <option value="">{placeholder}</option>
      {layers.map((layer) => (
        <option key={layer.id} value={layer.id}>
          {getLayerDisplayName(layer)}
        </option>
      ))}
    </select>
  );
}

/** A count above a list, the way every Esri result pane leads. */
export function ResultHeader({ label, count }: { label: string; count: number }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label} <span className="tabular-nums text-slate-400">({count})</span>
    </p>
  );
}

/** One clickable row in a result list. */
export function ResultRow({
  title,
  detail,
  onClick,
  active,
  action,
}: {
  title: string;
  detail?: string;
  onClick?: () => void;
  active?: boolean;
  action?: React.ReactNode;
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-medium text-slate-700">{title}</span>
        {detail && <span className="block truncate text-[11px] text-slate-400">{detail}</span>}
      </span>
      {action}
    </>
  );

  if (!onClick) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition ${
        active
          ? 'border-blue-300 bg-blue-50'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      {body}
    </button>
  );
}

/** A scrollable list, capped so a long result set cannot swallow the rail. */
export function ScrollList({ children, max = 240 }: { children: React.ReactNode; max?: number }) {
  return (
    <div className="space-y-1 overflow-y-auto pr-0.5" style={{ maxHeight: max }}>
      {children}
    </div>
  );
}

/** A read-only value with a copy button — coordinates, a share link, a total. */
export function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </span>
        <span className="block truncate font-mono text-[12px] tabular-nums text-slate-700">
          {value}
        </span>
      </span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          });
        }}
        className="flex-shrink-0 rounded px-1.5 py-1 text-[10.5px] font-semibold text-blue-600 transition hover:bg-blue-50"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
